import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useImmer } from "use-immer";
import { type z } from "zod";
import { http } from "~/services/api.services";

import {
  agentKeyedOn,
  getAgentTaskDetails,
} from "~/services/TGEPE/agent.services";
import { getGenericURL } from "~/utils/basic";
import { createZodSchema } from "~/utils/zod";

type SchemaType = {
  type: Record<string, { schema: z.ZodType }>;
};
interface IAgentStateContextProps {
  data?: any;
  setData: Dispatch<SetStateAction<any | null>>;
  formFields?: any;
  setFormFields: any;
  formSchema?: z.ZodType | null;
  allFormFields?: Record<string, Record<string, any>>;
  setAllFormFields?: Dispatch<
    SetStateAction<Record<string, Record<string, any>>>
  >;
  setFormSchema: any;
  tableFields?: any;
  setAllSchemas?: Dispatch<SetStateAction<SchemaType>>;
  allSchemas?: SchemaType;
  setTableFields?: any;
  allTableFields?: any;
  setAllTableFields?: any;
  values?: any;
  setValues: Dispatch<SetStateAction<any | null>>;
  documents?: any;
  setDocuments?: any;
  readOnly?: boolean;
  refreshData: () => void;
  isLoading: boolean;
  extractionVersion?: any;
  setExtractionVersion?: any;
  formStatus: "VALID" | "INVALID";
  isFormSaved: boolean;
  setIsFormSaved: Dispatch<SetStateAction<boolean>>;
}

interface IAgentStateProviderProps {
  children: ReactNode;
  isReadOnly?: boolean;
}

const AgentStateContext = createContext<IAgentStateContextProps>({
  setData: () => {},
  setFormFields: () => {},
  setFormSchema: () => {},
  setValues: () => {},
  setDocuments: () => {},
  refreshData: () => {},
  isLoading: true,
  formStatus: "INVALID",
  setIsFormSaved: () => {},
  isFormSaved: false
} satisfies IAgentStateContextProps);
export const AgentStateProvider = ({
  children,
  isReadOnly,
}: IAgentStateProviderProps) => {
  const [data, setData] = useState<any>(null);
  const [formFields, setFormFields] = useState<any>(null);
  const [allFormFields, setAllFormFields] = useState<Record<string, any>>({});
  const [allTableFields, setAllTableFields] = useState<Record<string, any>>({});
  const [tableFields, setTableFields] = useState<any>(null);
  const [values, setValues] = useImmer<any>(null);
  const [formSchema, setFormSchema] = useState<z.ZodType | null>(null);
  const [allSchemas, setAllSchemas] = useState<SchemaType>({} as SchemaType);
  const [documents, setDocuments] = useState<any>(null);
  const [readOnly] = useState<boolean>(isReadOnly ?? false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [extractionVersion, setExtractionVersion] = useState<any>(null);
  const [formStatus, setFormStatus] = useState<"VALID" | "INVALID">("VALID");
  const [isFormSaved, setIsFormSaved] = useState<boolean>(false);
  const roleid = localStorage.getItem("roleid") as string;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    if (!readOnly && (roleid == "1" || roleid == "15")) {
      try {
        // Fetch main data
        const { data: mainData, status } = await getAgentTaskDetails(roleid);

        if (status && (mainData as any)?.data != "") {
          // Pre-fetch zoning documents
          const zoningDocs = (mainData as any)?.data?.zoningdocuments;
          if (zoningDocs?.imageurl?.[0] && zoningDocs?.jsonurl?.[0]) {
            const [imageResponse, jsonResponse] = await Promise.all([
              http.get(getGenericURL(zoningDocs.imageurl[0])),
              http.get(getGenericURL(zoningDocs.jsonurl[0])),
            ]);

            const imageBlob = await imageResponse.blob();
            const imageUrl = URL.createObjectURL(imageBlob);
            const jsonData = await jsonResponse.json();

            // Merge pre-fetched data with main data
            const enrichedData = {
              ...(mainData as any)?.data,
              zoningdocuments: {
                ...zoningDocs,
                imageurl: [imageUrl],
                jsonData,
              },
            };

            setFormStatus("VALID");
            setData(enrichedData);

            // Handle agent keyed on
            const taskId = (mainData as any)?.data?.taskid;
            if (taskId) {
              const agentKeyedResponse = await agentKeyedOn(taskId, roleid);
              if (agentKeyedResponse.status) {
                console.log(
                  "agentKeyedOn successful:",
                  agentKeyedResponse.data
                );
              } else {
                console.error("agentKeyedOn failed:", agentKeyedResponse.error);
              }
            }
          } else {
            setFormStatus("VALID");
            setData((mainData as any)?.data);
          }
        } else if (status && (mainData as any).statusCode == 501) {
          setFormStatus("INVALID");
        } else {
          setData(null);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    }
  }, [readOnly, roleid]);

  // Clean up object URLs when data changes
  useEffect(() => {
    return () => {
      if (data?.zoningdocuments?.imageurl?.[0]?.startsWith("blob:")) {
        URL.revokeObjectURL(data.zoningdocuments.imageurl[0]);
      }
    };
  }, [data]);

  useEffect(() => {
    async function initializeData() {
      await fetchData();
    }
    initializeData();
  }, [fetchData]);

  useEffect(() => {
    if (!data && !readOnly) {
      const intervalId = setInterval(async () => {
        await fetchData();
      }, 30000);
      return () => clearInterval(intervalId);
    }
  }, [data, readOnly, fetchData]);

  useEffect(() => {
    if (
      data != null &&
      data instanceof Object &&
      data.data !== "" &&
      data.schema != null
    ) {
      const docType = data.dbdata?.header?.[0].shippingcode;

      const { schema }: { schema: Record<string, Record<string, any>> } = data;
      const allFormData = Object.keys(schema).reduce(
        (accl: any, ll: string) => {
          accl[ll] = schema[ll].groupList.reduce((acc: any, sl: string) => {
            const section = schema[ll].group.find(
              (prop: any) => prop.id === sl
            );
            if (section != null) {
              acc.push(section);
            }
            return acc;
          }, []);
          return accl;
        },
        {}
      );

      setAllFormFields(allFormData);

      setFormFields(allFormData[docType]);

      const allTableData = Object.keys(schema).reduce(
        (accl: any, ll: string) => {
          accl[ll] = schema[ll].tableList.reduce((acc: any, sl: string) => {
            const section = schema[ll].table.find(
              (prop: any) => prop.id === sl
            );
            if (section != null) {
              acc.push(section);
            }
            return acc;
          }, []);
          return accl;
        },
        {}
      );

      setAllTableFields(allTableData);
      setTableFields(allTableData[docType]);

      const allSchema: SchemaType = {
        type: {
          ...Object.keys(schema).reduce(
            (acc, key) => {
              acc[key] = {
                schema: createZodSchema(schema[key]),
              };
              return acc;
            },
            {} as Record<string, { schema: z.ZodType }>
          ),
        },
      };

      setAllSchemas(allSchema);

      setFormSchema(allSchema.type[docType].schema);
      setValues((data as any)?.dbdata);
      setDocuments((data as any)?.documents);
      setExtractionVersion((data as any)?.extractionsource);
    }
  }, [data, setValues]);

  const refreshData = async () => {
    await fetchData();
  };

  return (
    <AgentStateContext.Provider
      value={{
        data,
        formFields,
        formSchema,
        allSchemas,
        setAllSchemas,
        tableFields,
        values,
        setData,
        setAllFormFields,
        allFormFields,
        setFormFields,
        setTableFields,
        setFormSchema,
        allTableFields,
        setAllTableFields,
        setValues,
        setDocuments,
        setExtractionVersion,
        extractionVersion,
        documents,
        readOnly,
        refreshData,
        isLoading,
        formStatus,
        isFormSaved,
        setIsFormSaved
      }}
    >
      {children}
    </AgentStateContext.Provider>
  );
};

export function useAgent() {
  return useContext(AgentStateContext);
}
